<template>
  <div
    v-if="hasPermission('teams.view')"
  >
    <VTableRemote
      :iconHeader="hasPermission('teams.add') ? 'add' : null"
      :items="teams"
      isSelectable
      hideDefaultFooter
      :headers="tableHeaders"
      :itemActions="actions"
      :pagination="pagination"
      isCustomIcon
      :isSearchable="true"
      :title="$t('serviceProviders.pageName')"
      :centerColumns="['action']"
      :notSortableColumns="['action']"
      :sortableColumns="['name']"
      :loading="isFetching"
      :isInfinitScroll="true"
      @iconAction="onClickAction"
      @click-icon-header="onClickAdd"
      @select-row="openEditionModal"
      @sendQueryParams="fetchQuery"
    >
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </VTableRemote>
  </div>


  <div v-else>
    <NoPermissionAlert :message="$t('permissions.error')" />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import ServiceProviderForm from '@/components/forms/ServiceProviderForm';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import { serialize } from 'object-to-formdata';
import { isEmpty } from 'lodash';

export default {
    components: {
        VTableRemote,
        NoPermissionAlert,
    },
    data() {
        return {
            queryParams: {
                page: 1,
                perpage: 25,
                pagination: true,
                search: '',
            },
            pagination: {},
            isFetching: false,
            teams: [],
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'teamID',
        ]),
        tableHeaders() {
            return ['name'].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('teams.edit')) {
                actions.push({
                    name: this.$t('forms.labels.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('teams.remove')) {
                actions.push({
                    name: this.$t('forms.labels.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
    },
    async created() {
        await this.getTeams();
    },
    methods: {
        ...mapActions({
            reloadTeams: 'reloadTeams',
            reloadTeam: 'reloadTeam',
        }),
        onClickAction(serviceProvider, action) {
            if (!action && serviceProvider === 'add') {
                this.onClickAdd();
                return;
            }
            if (action.mode === 'edit') {
                this.openEditionModal(serviceProvider);
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('serviceProviders.remove'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('serviceProviders.confirmRemove') },
                    onConfirm: () => this.removeTeam(serviceProvider),
                    isForm: false,
                });
            }
        },
        async openEditionModal(serviceProvider) {
            const { data } = await this.$rq.getTeam(serviceProvider.id);

            this.$root.$emit('showModal', {
                title: this.$t('serviceProviders.update'),
                component: Vue.component('ServiceProviderForm', ServiceProviderForm),
                payload: { serviceProvider: data },
                onConfirm: requestPayload => this.updateTeam(serviceProvider.id, requestPayload),
            });
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('serviceProviders.add'),
                component: Vue.component('ServiceProviderForm', ServiceProviderForm),
                onConfirm: requestPayload => this.addTeam(requestPayload),
            });
        },
        preparePayload(payload) {
            const data = { ...payload };
            if (!isEmpty(payload.phoneNumber) && !payload.phoneNumber.valid) {
                data.phoneNumber = {};
            }
            if (('dateFormat' in data) && !data.dateFormat) { data.dateFormat = null; }
            const options = { allowEmptyArrays: true };
            return serialize(data, options);
        },
        async addTeam(requestPayload) {
            if (!requestPayload) {
                return;
            }
            let jsonPayload;
            // WITH LOGO
            if (this.hasFileUpload(requestPayload)) {
                const formData = new FormData();
                const cleanData = this.buildCleanTeamData(requestPayload);
                this.appendFormDataFields(formData, cleanData);
                formData.append('logo', requestPayload.logo);
                jsonPayload = formData;
            // WITHOUT LOGO
            } else {
                const payload = this.$utils.removeEmptyNullOrUndefined(requestPayload, true);
                jsonPayload = this.preparePayload(payload);
            }
            await this.$rq.addTeam(jsonPayload);
            this.$toasted.global.toast_success({
                message: this.$t('serviceProviders.added', { name: requestPayload.name }),
            });
            await this.fetchQuery({});
        },
        hasFileUpload(payload) {
            return payload && payload.logo && (
                payload.logo instanceof File
                || (payload.logo instanceof Object && payload.logo.name && payload.logo.size)
            );
        },
        buildCleanTeamData(requestPayload) {
            const allowedFields = [
                'name', 'email', 'address', 'province', 'country', 'city',
                'postalCode', 'streetNumber', 'appartmentNumber', 'lang',
                'optionalLangs', 'website', 'dateFormat',
            ];
            let cleanData = allowedFields.reduce((acc, field) => {
                if (requestPayload[field]) {
                    return { ...acc, [field]: requestPayload[field] };
                }
                return acc;
            }, {});
            if (requestPayload.phoneNumber && requestPayload.phoneNumber.valid) {
                cleanData = { ...cleanData, phoneNumber: requestPayload.phoneNumber };
            }
            return cleanData;
        },

        appendFormDataFields(formData, cleanData) {
            Object.keys(cleanData).forEach(key => {
                const value = cleanData[key];
                if (Array.isArray(value)) {
                    value.forEach(item => {
                        formData.append(key, item);
                    });
                } else {
                    formData.append(key, value);
                }
            });
        },
        async updateTeam(serviceProviderId, requestPayload) {
            if (!requestPayload) {
                return;
            }

            const allowedBodyFields = [
                'name', 'email', 'address', 'province', 'country', 'city',
                'postalCode', 'streetNumber', 'appartmentNumber', 'lang',
                'optionalLangs', 'website', 'warehouseEmail', 'dateFormat',
                'defaultStandardFamily', 'phoneNumber',
            ];

            const cleanData = {};
            allowedBodyFields.forEach(field => {
                if (field in requestPayload) {
                    cleanData[field] = requestPayload[field];
                }
            });

            const payload = this.preparePayload(cleanData);
            if (this.hasFileUpload(requestPayload)) {
                payload.append('logo', requestPayload.logo);
            }

            await this.$rq.updateTeam(serviceProviderId, payload);
            this.$toasted.global.toast_success({
                message: this.$t('serviceProviders.updated', { name: requestPayload.name }),
            });
            await this.fetchQuery({});
            await this.reloadTeam(this.teamID);
        },
        async removeTeam(serviceProvider) {
            await this.$rq.removeTeam(serviceProvider.id);
            this.$toasted.global.toast_success({
                message: this.$t('serviceProviders.removed', { name: serviceProvider.name }),
            });
            await this.fetchQuery({});
        },
        async getTeams() {
            this.isFetching = true;
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined({ ...this.queryParams }, true);
            this.loading = true;
            const { data } = await this.$rq.getTeams(cleanQueryParams);
            this.teams = [...this.teams, ...data.teams];
            this.pagination = {
                ...this.pagination,
                ...data.pagination,
            };
            this.isFetching = false;
        },

        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition && !this.isFetching) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getTeams();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async fetchQuery(queryParams) {
            this.queryParams = { ...this.queryParams, ...queryParams, page: 1 };
            this.pagination = {};
            this.teams = [];
            await this.getTeams();
        },
    },
};
</script>
